import { SVLoader } from '@/components/core';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import CheckDepositTransactionDetails from '@/components/transactions/CheckDepositTransactionDetails';
import CheckIssueTransactionDetails from '@/components/transactions/CheckIssueTransactionDetails';
import StandardTransactionDetails from '@/components/transactions/StandardTransactionDetails';
import { ROUTES } from '@/constants/routes';
import { useGetBankAccountDetails } from '@/hooks/requests/useBankAccounts';
import { useGetTransactionDetails } from '@/hooks/requests/useTransactions';
import { generateBankAccountDisplayName } from '@/utils/bankAccounts';
import { Card, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

export default function TransactionsDetailsPage() {
  const { bankAccountId = '', uuid = '' } = useParams();
  const { transactionDetails, isTransactionDetailsPending } =
    useGetTransactionDetails(bankAccountId, uuid);
  const { accountDetails, accountsDetailsIsPending } =
    useGetBankAccountDetails(uuid);

  if (isTransactionDetailsPending || accountsDetailsIsPending) {
    return (
      <DefaultLayout header="Transaction Details">
        <SVLoader />
      </DefaultLayout>
    );
  }

  if (!transactionDetails || !accountDetails) {
    return (
      <DefaultLayout header="Transaction Details">
        <Card>
          <Typography>
            Something went wrong retreiving the transaction. Our team has been
            notified.
          </Typography>
        </Card>
      </DefaultLayout>
    );
  }

  const { applicationType, business, primaryOwner, owners } = accountDetails;
  const { fullName, email } = primaryOwner.profile;
  const { name: businessName } = business || {};
  const totalOwners = owners.length;
  const bankAccountName = generateBankAccountDisplayName({
    applicationType,
    businessName,
    fullName,
    email,
    totalOwners,
  });

  const { description, transferType, checkDeposit, issuedCheck } =
    transactionDetails;

  const isCheckDeposit = transferType === 'check' && checkDeposit;
  const isCheckIssue = transferType === 'check' && issuedCheck;
  const isStandardTransaction = !isCheckDeposit && !isCheckIssue;

  return (
    <DefaultLayout
      header={description}
      breadcrumb={[
        { label: 'Bank Accounts', href: ROUTES.BANK_ACCOUNTS },
        {
          label: bankAccountName,
          href: `${ROUTES.BANK_ACCOUNTS}/${bankAccountId}`,
        },
        {
          label: 'Transactions',
          href: `${ROUTES.BANK_ACCOUNTS}/${bankAccountId}${ROUTES.TRANSACTIONS}`,
        },
        { label: description, isActive: true },
      ]}>
      {isCheckDeposit && (
        <CheckDepositTransactionDetails
          transactionDetails={transactionDetails}
        />
      )}
      {isCheckIssue && (
        <CheckIssueTransactionDetails transactionDetails={transactionDetails} />
      )}
      {isStandardTransaction && (
        <StandardTransactionDetails transactionDetails={transactionDetails} />
      )}
    </DefaultLayout>
  );
}
